<template>
  <div class="work__thumbs" ref="thumbsContainer">
    <div
      class="works__thumbs__indicator"
      :style="{
        '--indicator-inset': `${indicatorInset}px`,
        transform: `translate3D(0,${indicatorOffset}px,0)`,
        height: `${indicatorHeight}px`,
      }"
    ></div>
    <div
      ref="thumbItems"
      class="media__wrap"
      @click="scrollTo(i)"
      :class="{ 'is-boxed': el.boxed }"
      :style="{
        'background-color': el.background,
        'aspect-ratio': el.image.dimensions.width / el.image.dimensions.height,
      }"
      :data-media="i"
      v-for="(el, i) in medias"
    >
      <AppMedia :src="el?.image?.url"></AppMedia>
    </div>
  </div>
</template>

<script setup>
const { $Lenis } = useNuxtApp();
const props = defineProps({
  medias: { type: Array, default: [] },
});

const progress = ref(0); // Progress smooth (lerpato)
const targetProgress = ref(0); // Progress target (immediato)
const thumbsContainer = ref(null);
const thumbItems = ref([]);
const thumbMetrics = ref([]);
const remSize = ref(0);
let rafId = null; // ID per requestAnimationFrame
let resizeObserver = null;
let nativeScrollHandler = null;

const indicatorInset = computed(() => remSize.value || 16);

const interpolateThumbMetric = computed(() => {
  const metrics = thumbMetrics.value;
  const numThumbs = metrics.length;

  if (!numThumbs) {
    return {
      top: 0,
      height: 65,
    };
  }

  if (numThumbs === 1) {
    return metrics[0];
  }

  const thumbProgress = progress.value * (numThumbs - 1);
  const currentThumb = Math.floor(thumbProgress);
  const nextThumb = Math.min(currentThumb + 1, numThumbs - 1);
  const localProgress = thumbProgress - currentThumb;
  const lerp = (a, b, t) => a + (b - a) * t;

  return {
    top: lerp(metrics[currentThumb].top, metrics[nextThumb].top, localProgress),
    height: lerp(
      metrics[currentThumb].height,
      metrics[nextThumb].height,
      localProgress
    ),
  };
});

const indicatorOffset = computed(() => {
  return interpolateThumbMetric.value.top;
});

const indicatorHeight = computed(() => {
  return interpolateThumbMetric.value.height + indicatorInset.value * 2;
});

const scrollTo = index => {
  // Scrolla al media element con offset di 20vh
  const scrollHeight = $Lenis?.core?.dimensions?.scrollHeight;
  const offset =
    index === props.medias.length - 1
      ? scrollHeight
      : -(window.innerHeight / 100) * 20;

  $Lenis.core.scrollTo(`[data-media="${index}"]`, { offset });
};

const updateHeights = () => {
  if (!thumbItems.value?.length) return;
  remSize.value = parseFloat(
    getComputedStyle(document.documentElement).fontSize
  );
  thumbMetrics.value = thumbItems.value.map(el => ({
    top: el?.offsetTop || 0,
    height: el?.offsetHeight || 0,
  }));
};

/**
 * Calcola il progress dello scroll basandosi sulla posizione discreta dei media
 * - Progress 0: quando il primo media è a 20vh dal top
 * - Progress 1/(n-1): quando il secondo media è a 20vh dal top
 * - Progress 1: quando l'ultimo media è a 20vh dal top
 * - Interpolazione lineare tra i punti discreti
 */
const updateScrollProgress = () => {
  if (!thumbItems.value?.length) return;

  const numThumbs = props.medias.length;
  if (numThumbs === 0) return;

  // Ottieni tutti gli elementi [data-media] nel DOM principale (escludendo le thumbnails)
  // Le thumbnail sono dentro .work__thumbs, quindi escludiamo tutto ciò che sta lì dentro
  const allMediaElements = [];
  for (let i = 0; i < numThumbs; i++) {
    const mediaElements = document.querySelectorAll(`[data-media="${i}"]`);
    // Filtra per escludere le thumbnail (che sono dentro .work__thumbs)
    const mainMedia = Array.from(mediaElements).find(
      el => !el.closest('.work__thumbs')
    );
    if (mainMedia) {
      allMediaElements.push(mainMedia);
    }
  }

  if (allMediaElements.length !== numThumbs) return;

  if (numThumbs === 1) {
    targetProgress.value = 0;
    return;
  }

  // Calcola le posizioni di scroll target per ogni media (quando sono a 20vh dal top)
  const TARGET_OFFSET = window.innerHeight * 0.2;
  const currentScroll = window.scrollY || document.documentElement.scrollTop;
  const maxScroll = Math.max(
    0,
    document.documentElement.scrollHeight - window.innerHeight
  );
  const SCROLL_TOLERANCE = 2;

  // Array di posizioni dove ogni media è a 20vh dal top
  const scrollPositions = [];
  allMediaElements.forEach((media, index) => {
    const rawPosition = media.offsetTop - TARGET_OFFSET;
    const clampedPosition = Math.min(maxScroll, Math.max(0, rawPosition));

    if (index === 0) {
      scrollPositions.push(clampedPosition);
      return;
    }

    // Mantiene le posizioni monotone anche quando il clamp appiattisce il fondo pagina
    scrollPositions.push(
      Math.max(scrollPositions[index - 1], clampedPosition)
    );
  });

  // Trova tra quali due thumb ci troviamo
  let newProgress = 0;

  if (currentScroll <= scrollPositions[0]) {
    // Prima del primo media
    newProgress = 0;
  } else if (
    currentScroll >= scrollPositions[numThumbs - 1] ||
    currentScroll >= maxScroll - SCROLL_TOLERANCE
  ) {
    // Dopo l'ultimo media
    newProgress = 1;
  } else {
    // Tra due media: trova l'intervallo e interpola
    for (let i = 0; i < numThumbs - 1; i++) {
      const currentPos = scrollPositions[i];
      const nextPos = scrollPositions[i + 1];

      if (currentScroll >= currentPos && currentScroll <= nextPos) {
        // Siamo tra media[i] e media[i+1]
        if (nextPos === currentPos) {
          newProgress = (i + 1) / (numThumbs - 1);
          break;
        }

        const localProgress =
          (currentScroll - currentPos) / (nextPos - currentPos);
        // Progress discreto: thumb i → progress = i/(n-1), thumb i+1 → progress = (i+1)/(n-1)
        const progressAtI = i / (numThumbs - 1);
        const progressAtNext = (i + 1) / (numThumbs - 1);
        newProgress =
          progressAtI + (progressAtNext - progressAtI) * localProgress;
        break;
      }
    }
  }

  // Clamp progress tra 0 e 1
  targetProgress.value = Math.max(0, Math.min(1, newProgress));
};

/**
 * Loop di animazione per il smooth lerp del progress
 * Aggiorna continuamente progress verso targetProgress usando easing
 */
const EASING_FACTOR = 0.1; // Fattore di smoothing (0.05-0.15 per fluidità)
const THRESHOLD = 0.001; // Soglia minima per fermare il loop

const smoothUpdate = () => {
  const diff = targetProgress.value - progress.value;

  // Continua il lerp solo se la differenza è significativa
  if (Math.abs(diff) > THRESHOLD) {
    progress.value += diff * EASING_FACTOR;
    rafId = requestAnimationFrame(smoothUpdate);
  } else {
    // Snap al valore finale quando molto vicino
    progress.value = targetProgress.value;
    rafId = null;
  }
};

/**
 * Avvia il loop di smoothing se non è già attivo
 */
const startSmoothUpdate = () => {
  if (rafId === null) {
    rafId = requestAnimationFrame(smoothUpdate);
  }
};

let lenisUnsubscribe = null;
let loadListener = null;

onMounted(async () => {
  await nextTick();

  // Calcolo immediato delle altezze e progress
  updateHeights();
  updateScrollProgress();
  progress.value = targetProgress.value; // Prima volta setta direttamente

  // Usa Lenis per tracciare lo scroll in modo smooth
  if ($Lenis?.core) {
    lenisUnsubscribe = $Lenis.core.on('scroll', () => {
      updateScrollProgress();
      startSmoothUpdate(); // Avvia il loop di smoothing
    });
  } else {
    // Fallback a scroll nativo se Lenis non è disponibile
    nativeScrollHandler = () => {
      updateScrollProgress();
      startSmoothUpdate(); // Avvia il loop di smoothing
    };
    window.addEventListener('scroll', nativeScrollHandler, { passive: true });
  }

  // Re-calcolo dopo nextTick (DOM completamente aggiornato)
  await nextTick();
  updateHeights();
  updateScrollProgress();
  progress.value = targetProgress.value;

  // Re-calcolo dopo piccoli delay per layout stabilizzato
  setTimeout(() => {
    updateHeights();
    updateScrollProgress();
    progress.value = targetProgress.value;
  }, 100);

  setTimeout(() => {
    updateHeights();
    updateScrollProgress();
    progress.value = targetProgress.value;
  }, 300);

  // Re-calcolo dopo caricamento completo di tutte le risorse (immagini, ecc.)
  if (document.readyState === 'complete') {
    // Se già tutto caricato, esegui subito
    setTimeout(() => {
      updateHeights();
      updateScrollProgress();
      progress.value = targetProgress.value;
    }, 100);
  } else {
    // Altrimenti aspetta l'evento load
    loadListener = () => {
      setTimeout(() => {
        updateHeights();
        updateScrollProgress();
        progress.value = targetProgress.value;
      }, 100);
    };
    window.addEventListener('load', loadListener, { once: true });
  }

  resizeObserver = new ResizeObserver(() => {
    updateHeights();
    updateScrollProgress();
  });

  if (thumbsContainer.value) {
    resizeObserver.observe(thumbsContainer.value);
  }

  thumbItems.value.forEach(el => {
    if (el) {
      resizeObserver.observe(el);
    }
  });

  window.addEventListener('resize', updateHeights);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateHeights);

  // Cleanup requestAnimationFrame
  if (rafId !== null) {
    cancelAnimationFrame(rafId);
    rafId = null;
  }

  // Cleanup Lenis listener
  if (lenisUnsubscribe) {
    lenisUnsubscribe();
  }

  if (nativeScrollHandler) {
    window.removeEventListener('scroll', nativeScrollHandler);
    nativeScrollHandler = null;
  }

  // Cleanup load listener
  if (loadListener) {
    window.removeEventListener('load', loadListener);
  }

  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
});
</script>

<style lang="scss" scoped>
.work__thumbs {
  // margin-bottom: auto;
  // margin-left: var(--offset);
  // margin-top: auto;
  bottom: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
  left: calc(var(--offset) + col(5));
  position: fixed;
  margin-top: var(--top-offset);
  top: 0;
  height: fit-content;
}

.works__thumbs__indicator {
  border: 1.2px solid rgba(0, 0, 0, 0.3);
  left: calc(-1 * var(--indicator-inset, 1rem));
  position: absolute;
  pointer-events: none;
  top: calc(-1 * var(--indicator-inset, 1rem));
  right: calc(-1 * var(--indicator-inset, 1rem));
  z-index: 2;
}

.media__wrap {
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 7.5rem;
  cursor: pointer;

  &.is-boxed {
    padding: 15%;
  }
}

.media {
  background: var(--c-alt);
  filter: saturate(0);
  transition:
    filter 1s ease(outExpo),
    opacity 1s ease(outExpo);

  // max-height: 4.5rem;
  overflow: hidden;
  display: flex;
  position: relative;

  &:deep(img) {
    object-fit: cover;
  }

  &:hover {
    filter: saturate(1);
  }

  &.is-boxed {
    transform: scale(0.6);
  }
}
</style>
